/**
 * The GOOD BUILD OS icon set.
 *
 * Drawn rather than installed, because the board's icons are a consistent thin outline on a
 * 24 unit grid and no icon package matches that without being bent into shape. Every glyph
 * inherits `currentColor`, so the rail turns one lemon when it becomes the current destination
 * and nothing else has to know about it.
 */

import * as React from 'react';

export type IconName =
  | 'home'
  | 'games'
  | 'library'
  | 'studio'
  | 'search'
  | 'settings'
  | 'play'
  | 'pause'
  | 'more'
  | 'bell'
  | 'user'
  | 'crew'
  | 'download'
  | 'admin'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-down'
  | 'chevron-up'
  | 'check'
  | 'close'
  | 'external'
  | 'plus'
  | 'refresh'
  | 'windows'
  | 'linux'
  | 'android'
  | 'gamepad'
  | 'tv'
  | 'shield'
  | 'warning'
  | 'code'
  | 'file'
  | 'folder'
  | 'copy'
  | 'save'
  | 'book';

const PATHS: Record<IconName, React.ReactNode> = {
  home: (
    <>
      <path d="M3.5 10.2 12 3.6l8.5 6.6V20a1 1 0 0 1-1 1h-4.6v-6.1H9.1V21H4.5a1 1 0 0 1-1-1Z" />
    </>
  ),
  games: (
    <>
      <path d="M7.3 7.5h9.4a4.3 4.3 0 0 1 4.2 3.5l.8 4.4a2.6 2.6 0 0 1-4.7 1.9l-1.2-1.7H8.2L7 17.3a2.6 2.6 0 0 1-4.7-1.9l.8-4.4a4.3 4.3 0 0 1 4.2-3.5Z" />
      <path d="M7.3 11v2.4M6.1 12.2h2.4M15.6 11.6h.01M17.9 13.3h.01" />
    </>
  ),
  library: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </>
  ),
  studio: (
    <>
      <circle cx="9" cy="8.2" r="3.1" />
      <path d="M3.4 20a5.6 5.6 0 0 1 11.2 0" />
      <path d="M16 5.6a3.1 3.1 0 0 1 0 5.6M17.6 14.9A5.6 5.6 0 0 1 20.6 20" />
    </>
  ),
  search: (
    <>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.6 15.6 4.4 4.4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.2 14.6a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.6 1.1v.3a1.8 1.8 0 1 1-3.6 0v-.2a1.5 1.5 0 0 0-2.7-1.1l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1.1-2.6h-.3a1.8 1.8 0 1 1 0-3.6h.2a1.5 1.5 0 0 0 1.1-2.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3h.1a1.5 1.5 0 0 0 .9-1.4v-.3a1.8 1.8 0 1 1 3.6 0v.2a1.5 1.5 0 0 0 2.6 1.1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7v.1a1.5 1.5 0 0 0 1.4.9h.3a1.8 1.8 0 1 1 0 3.6h-.2a1.5 1.5 0 0 0-1.4.9Z" />
    </>
  ),
  play: <path d="M7.5 4.8 19 12 7.5 19.2Z" />,
  /* Solid, like play: the two read as one control on a download, and an outlined pause beside
     a filled play looks like the disabled one of the pair. */
  pause: (
    <>
      <path d="M8 5h3v14H8z" />
      <path d="M13 5h3v14h-3z" />
    </>
  ),
  /* Three dots on the round cap the rest of the set is drawn with, so the tile's menu button
     is the same weight as everything beside it. */
  more: (
    <>
      <path d="M6 12h.01" />
      <path d="M12 12h.01" />
      <path d="M18 12h.01" />
    </>
  ),
  bell: (
    <>
      <path d="M18.2 16.3V10a6.2 6.2 0 1 0-12.4 0v6.3L4 18.6h16Z" />
      <path d="M10 21.2a2.3 2.3 0 0 0 4 0" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20.5a7.2 7.2 0 0 1 14.4 0" />
    </>
  ),
  crew: (
    <>
      <circle cx="7.6" cy="8.4" r="2.9" />
      <circle cx="16.4" cy="8.4" r="2.9" />
      <path d="M2.6 19.4a5 5 0 0 1 10 0M11.4 19.4a5 5 0 0 1 10 0" />
    </>
  ),
  download: (
    <>
      <path d="M12 3.6v11.6M7.6 11.2 12 15.6l4.4-4.4" />
      <path d="M4.4 17.4v1.6a1.4 1.4 0 0 0 1.4 1.4h12.4a1.4 1.4 0 0 0 1.4-1.4v-1.6" />
    </>
  ),
  admin: (
    <>
      <path d="M12 3.2 4.8 6v6.1c0 4.4 3 8 7.2 9.1 4.2-1.1 7.2-4.7 7.2-9.1V6Z" />
      <path d="m8.9 11.9 2.2 2.2 4-4.3" />
    </>
  ),
  'chevron-right': <path d="m9.5 5 7 7-7 7" />,
  'chevron-left': <path d="m14.5 5-7 7 7 7" />,
  'chevron-down': <path d="m5 9.5 7 7 7-7" />,
  'chevron-up': <path d="m5 14.5 7-7 7 7" />,
  check: <path d="m4.8 12.5 4.6 4.6L19.2 7.3" />,
  close: <path d="M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" />,
  external: (
    <>
      <path d="M13.6 4.4h6v6M19.2 4.8 10.4 13.6" />
      <path d="M18.4 14v5a1.4 1.4 0 0 1-1.4 1.4H5a1.4 1.4 0 0 1-1.4-1.4V7a1.4 1.4 0 0 1 1.4-1.4h5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  refresh: (
    <>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20.2 4v4.6h-4.6" />
    </>
  ),

  /* The three platforms, on the same 24 unit grid as everything else. They are marks rather
     than logos: a four pane window, a penguin reduced to its silhouette, a handset. */
  windows: (
    <>
      <path d="M3.4 6.3l7.4-1.1v6.1H3.4ZM12.6 5 20.6 3.8v7.5h-8ZM3.4 12.7h7.4v6.1L3.4 17.7ZM12.6 12.7h8v7.5L12.6 19Z" />
    </>
  ),
  linux: (
    <>
      <path d="M9.4 3.9a2.6 2.6 0 0 1 5.2 0v3.2c0 1.5 3 3.9 3 7.6a5.6 5.6 0 0 1-11.2 0c0-3.7 3-6.1 3-7.6Z" />
      <path d="M10.4 5.4h.01M13.6 5.4h.01" />
      <path d="M10.8 8.6 12 9.7l1.2-1.1" />
    </>
  ),
  android: (
    <>
      <rect x="6.2" y="3.4" width="11.6" height="17.2" rx="2.2" />
      <path d="M10.4 17.6h3.2" />
    </>
  ),

  /* Three marks for what the app is, rather than where it runs. */
  gamepad: (
    <>
      <path d="M7.6 7.8h8.8a4 4 0 0 1 3.9 3.2l.7 4a2.4 2.4 0 0 1-4.4 1.7l-1.1-1.5H8.5L7.4 16.7A2.4 2.4 0 0 1 3 15l.7-4a4 4 0 0 1 3.9-3.2Z" />
      <path d="M7.6 11v2.2M6.5 12.1h2.2M16 11.6h.01M18 13.2h.01" />
    </>
  ),
  tv: (
    <>
      <rect x="2.8" y="5.2" width="18.4" height="12.2" rx="1.8" />
      <path d="M8.4 20.6h7.2M9.2 2.6 12 5.2l2.8-2.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.2 4.8 6v6.1c0 4.4 3 8 7.2 9.1 4.2-1.1 7.2-4.7 7.2-9.1V6Z" />
    </>
  ),
  /* The triangle is drawn open like the rest of the set. The bar and the dot are separate
     strokes so the dot keeps its round cap at every size. */
  warning: (
    <>
      <path d="M12 3.6 2.4 20.4h19.2Z" />
      <path d="M12 9.6v4.6" />
      <path d="M12 17.4h.01" />
    </>
  ),
  /* The script editor's set. Angle brackets around a slash is what a language is drawn as
     everywhere, and the rest are the file, the folder and the two things you do with them. */
  code: (
    <>
      <path d="M8.6 8.4 4.5 12l4.1 3.6" />
      <path d="M15.4 8.4 19.5 12l-4.1 3.6" />
      <path d="M13.4 5.4 10.6 18.6" />
    </>
  ),
  file: (
    <>
      <path d="M13.4 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.6Z" />
      <path d="M13.4 3.5v5.1h5.1" />
    </>
  ),
  folder: (
    <>
      <path d="M3.5 6.6c0-.9.7-1.6 1.6-1.6h3.7l2 2.4h8.1c.9 0 1.6.7 1.6 1.6v8.4c0 .9-.7 1.6-1.6 1.6H5.1a1.6 1.6 0 0 1-1.6-1.6Z" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="12" height="12" rx="1.6" />
      <path d="M15.5 5.6V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v9c0 .8.7 1.5 1.5 1.5h.6" />
    </>
  ),
  save: (
    <>
      <path d="M4.5 5.6c0-.6.5-1.1 1.1-1.1h10.3l3.6 3.6v10.3c0 .6-.5 1.1-1.1 1.1H5.6a1.1 1.1 0 0 1-1.1-1.1Z" />
      <path d="M8.2 4.5v5h7.2v-5" />
      <path d="M7.6 19.5v-5.8h8.8v5.8" />
    </>
  ),
  book: (
    <>
      <path d="M4.5 4.6c0-.6.5-1.1 1.1-1.1H11a2 2 0 0 1 2 2v14a1.6 1.6 0 0 0-1.6-1.6H5.6a1.1 1.1 0 0 1-1.1-1.1Z" />
      <path d="M19.5 4.6c0-.6-.5-1.1-1.1-1.1H13a2 2 0 0 0-2 2v14a1.6 1.6 0 0 1 1.6-1.6h5.8c.6 0 1.1-.5 1.1-1.1Z" />
    </>
  ),
};

/** Icons that are drawn as a solid shape, because the board draws them that way. */
const FILLED = new Set<IconName>(['play', 'pause', 'home', 'windows']);

export interface IconProps {
  name: IconName;
  /** In pixels. Defaults to 20, which is the rail size on the board. */
  size?: number;
  className?: string;
}

export function Icon({ name, size = 20, className }: IconProps) {
  const filled = FILLED.has(name);
  return (
    <svg
      className={['gb-icon', className].filter(Boolean).join(' ')}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
